import {useCallback, useEffect, useRef, useState} from "react";
import {getApiBase} from "@/lib/config";
import {apiFetch} from "@/lib/api";
import {truncate} from "@/lib/markdown";
// getUserId import removed — ownership is cookie-derived server-side now.
import {type GenUIPayload, isValidGenUI} from "@/lib/gen-ui";
import {extractProjects} from "@/lib/project-mentions";
import {extractReviewRefs, type ReviewRef} from "@/lib/review-refs";
import {listWorkspaces, type WsWorkspace} from "@/lib/workspaceApi";
import {STORAGE_CHAT, STORAGE_EVENTS} from "@/constants/storage";

export type ChatMessage =
    | { role: "user"; content: string }
    | { role: "assistant"; content: string; id?: string }
    | { role: "error"; content: string }
    | { role: "tool_trace"; name: string; inputSummary: string; outputSummary: string; projects?: string[]; refs?: ReviewRef[] }
    | { role: "choice_result"; content: string; prompt: string; options: ChoiceOption[]; selected: string[]; otherText?: string }
    | { role: "form_result"; content: string; title?: string; reason?: string; questions: FormQuestion[]; answers: FormAnswers }
    // propose_result freezes a resolved workspace_propose_* turn so the
    // chat history shows a friendly "we did X" card instead of a raw
    // tool_use+tool_result pair. toolName is the workspace_propose_<variant>;
    // args is the parsed tool_use input (project_name, field, etc.);
    // selected is the value the user picked on the live chip; applied is
    // the post-dispatch state from the backend (e.g. "added_to_shortlist");
    // propertyId / destinationWorkspaceId are populated post-hydration so
    // the card can render a deep-link into the affected workspace surface.
    | { role: "propose_result"; toolName: string; args: Record<string, unknown>; selected: string; applied: string; propertyId?: string; destinationWorkspaceId?: string }
    | { role: "gen_ui"; payload: GenUIPayload };

// Server-side conversations are the source of truth; the FE only persists
// the conversation_id and hydrates history from the backend on mount.
//
// Keys are namespaced per mode because a conversation's mode is locked at
// creation time (see backend/internal/httpapi/server.go resolveChat — it
// reads mode off the persisted row and ignores the request's mode for
// existing conversations). Without per-mode namespacing, switching from
// /chat (consumer) to /pro/copilot (agent) would carry the consumer
// conv_id over and the backend would silently keep using the consumer
// system prompt despite the FE asking for agent.
const CONV_KEY_PREFIX = STORAGE_CHAT.conversationIdPrefix;
// Records the in-flight {conversation_id, message_id} pair so a tab refresh
// can rejoin the detached producer instead of leaving the user staring at
// an unanswered message until the next manual reload.
const PENDING_KEY_PREFIX = STORAGE_CHAT.pendingStreamPrefix;

function convKey(mode?: string): string {
    return `${CONV_KEY_PREFIX}.${mode || "default"}.v1`;
}

function pendingKey(mode?: string): string {
    return `${PENDING_KEY_PREFIX}.${mode || "default"}.v1`;
}

type ServerMessage = {
    id: string;
    role: string;
    content: string;
    tool_name?: string;
    tool_input?: string;
    tool_output?: string;
    seq: number;
    choice_prompt?: string;
    choice_options?: ChoiceOption[];
    choice_selected?: string[];
    choice_other?: string;
    form_title?: string;
    form_reason?: string;
    form_questions?: FormQuestion[];
    form_answers?: FormAnswers;
    // Propose fields are populated for role="propose_result" rows.
    propose_tool_name?: string;
    propose_args?: Record<string, unknown>;
    propose_selected?: string;
    propose_applied?: string;
    propose_property_id?: string;
    propose_destination_workspace_id?: string;
};

type PendingStream = { conversationId: string; messageId: string };

function loadConversationId(mode?: string): string | null {
    try {
        return localStorage.getItem(convKey(mode));
    } catch {
        return null;
    }
}

function saveConversationId(mode: string | undefined, id: string | null) {
    try {
        const key = convKey(mode);
        if (id) localStorage.setItem(key, id);
        else localStorage.removeItem(key);
    } catch {
        // localStorage unavailable — we just lose persistence across reloads
    }
}

function loadPendingStream(mode?: string): PendingStream | null {
    try {
        const raw = localStorage.getItem(pendingKey(mode));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.conversationId === "string" && typeof parsed.messageId === "string") {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
}

function savePendingStream(mode: string | undefined, p: PendingStream | null) {
    try {
        const key = pendingKey(mode);
        if (p) localStorage.setItem(key, JSON.stringify(p));
        else localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

function newMessageId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    const rand = () => Math.floor(Math.random() * 1e9).toString(36);
    return `${Date.now().toString(36)}-${rand()}${rand()}`;
}

function hydrateFromServer(msgs: ServerMessage[]): ChatMessage[] {
    return msgs.map<ChatMessage>((m) => {
        if (m.role === "tool_trace") {
            // present_data traces become gen_ui messages (the payload is the tool_input JSON).
            if (m.tool_name === "present_data" && m.tool_input) {
                try {
                    const parsed = JSON.parse(m.tool_input);
                    if (isValidGenUI(parsed)) {
                        return {role: "gen_ui", payload: parsed};
                    }
                } catch {
                    // fall through to regular tool_trace if JSON is malformed
                }
            }
            // Extract project mentions + web-search refs BEFORE truncating
            // tool_output — a multi-candidate find_property_location result
            // or an 8-row web_search response can exceed 600 chars and we'd
            // lose later entries.
            const projects = extractProjects(m.tool_name || "", m.tool_output || "");
            const refs = extractReviewRefs(m.tool_name || "", m.tool_output || "");
            return {
                role: "tool_trace",
                name: m.tool_name || "tool",
                inputSummary: truncate(m.tool_input || "", 140),
                outputSummary: truncate(m.tool_output || "", 600),
                projects: projects.length > 0 ? projects : undefined,
                refs: refs.length > 0 ? refs : undefined,
            };
        }
        if (m.role === "choice_result") {
            return {
                role: "choice_result",
                content: m.content,
                prompt: m.choice_prompt ?? "",
                options: m.choice_options ?? [],
                selected: m.choice_selected ?? [],
                otherText: m.choice_other,
            };
        }
        if (m.role === "form_result") {
            // form_questions can be malformed in two ways `?? []` doesn't
            // catch:
            //   1. Server preserves the LLM's raw tool_use input, and the
            //      model occasionally emits questions as a JSON object
            //      instead of an array.
            //   2. Older conversation rows pre-date the array shape.
            // Either way the HITLFormCard does questions.every(...) and
            // crashes the whole /chat/:id render. Coerce with
            // Array.isArray so a bad historical row degrades to an empty
            // form instead of nuking the conversation.
            return {
                role: "form_result",
                content: m.content,
                title: m.form_title,
                reason: m.form_reason,
                questions: Array.isArray(m.form_questions) ? m.form_questions : [],
                answers: m.form_answers ?? {},
            };
        }
        if (m.role === "propose_result") {
            return {
                role: "propose_result",
                toolName: m.propose_tool_name ?? "",
                args: m.propose_args ?? {},
                selected: m.propose_selected ?? "",
                applied: m.propose_applied ?? "",
                propertyId: m.propose_property_id || undefined,
                destinationWorkspaceId: m.propose_destination_workspace_id || undefined,
            };
        }
        if (m.role === "user") return {role: "user", content: m.content};
        return {role: "assistant", content: m.content, id: m.id};
    });
}

// Used after hydration to decide whether a pending stream still needs
// resuming. Tool traces are ignored because BE persists them before the
// assistant text turn, so a turn with only traces is still "unanswered".
function lastNonTraceIsUser(h: ChatMessage[]): boolean {
    for (let i = h.length - 1; i >= 0; i--) {
        const m = h[i];
        if (m.role === "tool_trace" || m.role === "choice_result" || m.role === "form_result" || m.role === "propose_result" || m.role === "gen_ui") continue;
        return m.role === "user";
    }
    return false;
}

export type ChoiceOption = {
    label: string;
    value: string;
};

// Sentinel value the FE sends back when the user picks the LLM-determined
// "Other" escape hatch. Mirrors backend constants.HITLChoiceOtherValue.
export const CHOICE_OTHER_VALUE = "__other__";

export type FormQuestion = {
    id: string;
    type: "text" | "single_choice" | "multi_choice";
    prompt: string;
    required?: boolean;
    options?: ChoiceOption[];
    allow_other?: boolean;
    placeholder?: string;
};

// Per-question answer shape stored in tool_result and rehydrated history.
//   text          → { text: "..." }
//   single_choice → { value: "...", other_text?: "..." }
//   multi_choice  → { values: ["..."], other_text?: "..." }
export type FormAnswerText = { text: string };
export type FormAnswerSingle = { value: string; other_text?: string };
export type FormAnswerMulti = { values: string[]; other_text?: string };
export type FormAnswer = FormAnswerText | FormAnswerSingle | FormAnswerMulti;
export type FormAnswers = Record<string, FormAnswer>;

export type PendingInteraction = {
    toolUseId: string;
    // kind distinguishes how the pause should be resolved:
    //   "hitl"               — free-form text via respond()
    //   "hitl_choice"        — structured option selection via respondChoice()
    //   "hitl_form"          — multi-question form via respondForm()
    //   "approval"           — approve/deny via approve()/deny()
    //   "workspace_propose"  — chat-side proposal to mutate the workspace;
    //                          resolved by respondChoice() with the chosen
    //                          action value (add/skip/update_workspace/...).
    kind: "hitl" | "hitl_choice" | "hitl_form" | "approval" | "workspace_propose";
    toolName?: string;
    prompt?: string;
    // options/multiple/allowOther are populated for kind="hitl_choice".
    options?: ChoiceOption[];
    multiple?: boolean;
    allowOther?: boolean;
    // formTitle/formReason/formQuestions are populated for kind="hitl_form".
    formTitle?: string;
    formReason?: string;
    formQuestions?: FormQuestion[];
    // proposeArgs carries the parsed tool_input for kind="workspace_propose".
    // The chip renderer reads project_name / field / priority_label /
    // property_id / kind / text + reason + evidence_quote depending on
    // the toolName variant.
    proposeArgs?: Record<string, unknown>;
};

// PendingResponse is the shape of GET /conversations/{id}/pending that both
// the mount-hydrate and the runtime open() switch parse.
export type PendingResponse = {
    pending?: {
        tool_use_id?: string;
        kind?: string;
        tool_name?: string;
        prompt?: string;
        tool_input?: {
            prompt?: string;
            options?: ChoiceOption[];
            multiple?: boolean;
            allow_other?: boolean;
            title?: string;
            reason?: string;
            questions?: FormQuestion[];
            [key: string]: unknown;
        } | null;
    } | null;
};

// pendingFromResponse maps a /pending response to a PendingInteraction (or
// null when there is no live pause). Shared by the mount hydrate and the
// runtime open() switch so a reopened conversation restores its HITL /
// approval / propose pause identically to a page refresh (PROP-191).
export function pendingFromResponse(pb: PendingResponse): PendingInteraction | null {
    if (!pb.pending || !pb.pending.tool_use_id) return null;
    const kind: PendingInteraction["kind"] =
        pb.pending.kind === "approval" ? "approval" :
            pb.pending.kind === "hitl_choice" ? "hitl_choice" :
                pb.pending.kind === "hitl_form" ? "hitl_form" :
                    pb.pending.kind === "workspace_propose" ? "workspace_propose" : "hitl";
    const ti = pb.pending.tool_input ?? undefined;
    return {
        toolUseId: pb.pending.tool_use_id,
        kind,
        toolName: pb.pending.tool_name,
        prompt: pb.pending.prompt,
        options: kind === "hitl_choice" ? (ti?.options ?? undefined) : undefined,
        multiple: kind === "hitl_choice" ? (ti?.multiple ?? undefined) : undefined,
        allowOther: kind === "hitl_choice" ? !!ti?.allow_other : undefined,
        formTitle: kind === "hitl_form" ? ti?.title : undefined,
        formReason: kind === "hitl_form" ? ti?.reason : undefined,
        formQuestions: kind === "hitl_form" ? ti?.questions : undefined,
        proposeArgs: kind === "workspace_propose" ? ti : undefined,
    };
}

export type LiveFlowItem =
    | { kind: "text"; content: string }
    | { kind: "tool"; name: string; projects?: string[]; refs?: ReviewRef[] }
    | { kind: "active"; name: string }
    | { kind: "thinking" }
    | { kind: "gen_ui"; payload: GenUIPayload };

export interface UseChatState {
    history: ChatMessage[];
    liveFlow: LiveFlowItem[];
    sending: boolean;
    pending: PendingInteraction | null;
    conversationId: string | null;
    // workspaces are the signed-in user's active workspaces, sorted by
    // updated_at DESC (same order the BE uses for default selection).
    // Empty on first render; lazy-loaded when a propose pending appears.
    // Used by the WorkspaceProposeCard to show the destination workspace
    // name and let the user retarget when they have more than one.
    workspaces: WsWorkspace[];
    // currentWorkspaceId is the workspace_id stored on the current
    // conversation row (BE-side ground truth, not derived). "" when the
    // conversation is anonymous, pre-Ship-1, or hasn't loaded yet.
    currentWorkspaceId: string;
    send: (text: string) => Promise<boolean>;
    respond: (text: string) => Promise<boolean>;
    // respondChoice resolves a hitl_choice OR workspace_propose pause.
    // For workspace_propose, optional workspaceId redirects the
    // mutation to a different workspace owned by the same user.
    respondChoice: (selected: string[], otherText?: string, workspaceId?: string) => Promise<boolean>;
    respondForm: (answers: FormAnswers) => Promise<boolean>;
    approve: (reason?: string) => Promise<boolean>;
    deny: (reason?: string) => Promise<boolean>;
    stop: () => void;
    clear: () => void;
    // open re-points the hook at an existing conversation and re-hydrates
    // it (history tray "reopen archived thread" — PROP-191).
    open: (id: string) => Promise<void>;
    retry: () => Promise<boolean>;
    canRetry: boolean;
    assistantReplyCount: number;
}

// initialConversationId controls which conversation this hook instance owns:
//   string  → load and continue that specific conversation
//   null    → start fresh (ignore localStorage, new conversation on first send)
//   undefined → legacy: use whatever is in localStorage
//
// workspaceId, when provided, tells the BE which workspace to link the
// new conversation to. Omitted → the BE falls back to the user's
// default workspace (for signed-in users) or leaves the link NULL
// (anonymous). The link is stamped on conversation creation only;
// existing conversations keep their stored workspace_id regardless of
// what this hook passes on later turns.
export function useChat(mode?: string, initialConversationId?: string | null, workspaceId?: string): UseChatState {
    const [history, setHistory] = useState<ChatMessage[]>([]);
    const [liveFlow, setLiveFlow] = useState<LiveFlowItem[]>([]);
    const [sending, setSending] = useState(false);
    const [pending, setPending] = useState<PendingInteraction | null>(null);
    const [workspaces, setWorkspaces] = useState<WsWorkspace[]>([]);
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string>("");

    const conversationIdRef = useRef<string | null>(
        initialConversationId === null ? null
            : initialConversationId !== undefined ? initialConversationId
                : loadConversationId(mode)
    );
    // lastTerminalErrorRef carries the most recent terminalError out of
    // runStream so send() can decide whether to auto-retry without
    // changing runStream's boolean signature. Cleared at the start of
    // every runStream invocation.
    const lastTerminalErrorRef = useRef<string>("");
    const currentStream = useRef<{
        id: string;
        lastDataAt: number;
        abort: AbortController | null;
        stopped: boolean;
        // discarded: set by clear() when the caller is throwing the
        // current thread away (e.g. "+ New chat" mid-stream). Tells the
        // runStream finally block NOT to flush its accumulated nextHistory
        // back into React state — otherwise the late finally write would
        // overwrite the empty history clear() just set.
        discarded: boolean;
        // gotChunk turns true after the first SSE chunk lands. We only let the
        // visibility-change watchdog abort streams that were already mid-flow —
        // pre-first-token thinking pauses (often 3–6s on long Chinese prompts)
        // would otherwise be killed if the user briefly switched tabs and back.
        gotChunk: boolean;
    } | null>(null);

    useEffect(() => {
        function onVis() {
            const cs = currentStream.current;
            if (document.visibilityState !== "visible" || !cs) return;
            // Only abort if (a) the producer already started streaming data and
            // (b) the connection has been idle for a long time — likely a true
            // dropped connection rather than the LLM's pre-first-token pause.
            if (!cs.gotChunk) return;
            const idle = Date.now() - cs.lastDataAt;
            if (idle > 30000 && cs.abort) cs.abort.abort();
        }

        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, []);

    // Lazy-load the user's workspaces + current conversation workspace_id
    // when a workspace_propose pause appears. Only fetched once per pause
    // since the chip's lifecycle is short. listWorkspaces returns the
    // user's active workspaces sorted by updated_at DESC (matches BE
    // default selection); GET /conversations/{id} returns the row's
    // workspace_id (the explicit linkage, not the default heuristic).
    useEffect(() => {
        if (!pending || pending.kind !== "workspace_propose") return;
        let cancelled = false;
        (async () => {
            try {
                const list = await listWorkspaces();
                if (!cancelled) setWorkspaces(list);
            } catch { /* ignore — chip will render without picker */ }
            const cid = conversationIdRef.current;
            if (!cid) return;
            try {
                const r = await apiFetch(`/conversations/${encodeURIComponent(cid)}`);
                if (!r.ok) return;
                const body = (await r.json()) as { workspace_id?: string };
                if (!cancelled && body.workspace_id) setCurrentWorkspaceId(body.workspace_id);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [pending]);

    const clear = useCallback(() => {
        // If a stream is mid-flight, abort + tag discarded so its
        // finally block doesn't write its accumulated nextHistory back
        // on top of the empty history we're about to set. (Without
        // this, "+ New chat" mid-stream would briefly empty the thread
        // and then snap back to the partial reply when the abort
        // unwound through finally.)
        const cs = currentStream.current;
        if (cs) {
            cs.stopped = true;
            cs.discarded = true;
            cs.abort?.abort();
        }
        setHistory([]);
        setLiveFlow([]);
        setPending(null);
        conversationIdRef.current = null;
        saveConversationId(mode, null);
        savePendingStream(mode, null);
    }, [mode]);

    // open switches this hook to an existing conversation: it tears down any
    // live stream, points at the new id, and re-hydrates that thread's
    // messages + pending from the server. The history tray uses this to
    // REOPEN an archived thread instead of clearing + re-sending its
    // (server-truncated) title as a brand-new message (PROP-191). The
    // post-open view matches a page refresh on that conversation (CLAUDE.md
    // "optimistic UI must equal post-refresh UI").
    const open = useCallback(async (id: string) => {
        const target = (id || "").trim();
        if (!target) return;
        const cs = currentStream.current;
        if (cs) {
            cs.stopped = true;
            cs.discarded = true;
            cs.abort?.abort();
        }
        conversationIdRef.current = target;
        saveConversationId(mode, target);
        savePendingStream(mode, null);
        setLiveFlow([]);
        setPending(null);
        setHistory([]);
        setSending(false);
        try {
            const [msgsRes, pendingRes] = await Promise.all([
                apiFetch(`/conversations/${encodeURIComponent(target)}/messages`),
                apiFetch(`/conversations/${encodeURIComponent(target)}/pending`).catch(() => null),
            ]);
            // A concurrent open()/clear()/send() may have moved us on.
            if (conversationIdRef.current !== target) return;
            if (!msgsRes.ok) {
                if (msgsRes.status === 404) {
                    conversationIdRef.current = null;
                    saveConversationId(mode, null);
                }
                return;
            }
            const body = (await msgsRes.json()) as { messages?: ServerMessage[] };
            if (body.messages) setHistory(hydrateFromServer(body.messages));
            if (pendingRes && pendingRes.ok) {
                const p = pendingFromResponse((await pendingRes.json()) as PendingResponse);
                if (p) setPending(p);
            }
        } catch {
            // Network failure — leave the (empty) thread; the next send still works.
        }
    }, [mode]);

    // runStream drives the resumable SSE tail loop for one assistant turn.
    // Both a fresh send() (firstBody carries the user message) and a
    // post-refresh rejoin (firstBody="{}") use this — the only difference
    // is whether the BE is being asked to start a new producer or just
    // tail an existing one.
    const runStream = useCallback(async (opts: {
        messageId: string;
        firstBody: string;
        baseline: ChatMessage[];
    }): Promise<boolean> => {
        const {messageId, firstBody} = opts;
        let nextHistory = opts.baseline;

        setSending(true);
        lastTerminalErrorRef.current = "";
        const liveFlowArr: LiveFlowItem[] = [{kind: "thinking"}];
        setLiveFlow([...liveFlowArr]);

        const base = getApiBase();
        let sinceOffset = 0;
        let gotReply = false;
        let terminalError: string | null = null;
        let resumeAttempts = 0;
        const MAX_RESUMES = 8;

        currentStream.current = {id: messageId, lastDataAt: Date.now(), abort: null, stopped: false, discarded: false, gotChunk: false};
        // If we already know the conversation, pin the pending marker now so
        // a refresh in the next few ms still has something to rejoin with.
        // First-turn pending gets upgraded below once X-Conversation-Id lands.
        if (conversationIdRef.current) {
            savePendingStream(mode, {conversationId: conversationIdRef.current, messageId});
        }

        // Commit whatever is in liveFlowArr to nextHistory and reset the array.
        // Text segments and tool traces are committed in their arrival order so
        // the history mirrors the interleaved live view exactly.
        const commitPartial = (note?: string) => {
            const hasItems = liveFlowArr.some(x => x.kind === "text" || x.kind === "tool" || x.kind === "gen_ui");
            if (!hasItems) return false;
            const newMsgs: ChatMessage[] = [];
            for (const item of liveFlowArr) {
                if (item.kind === "text") newMsgs.push({role: "assistant", content: item.content});
                if (item.kind === "tool") newMsgs.push({
                    role: "tool_trace",
                    name: item.name,
                    inputSummary: "",
                    outputSummary: "",
                    projects: item.projects,
                    refs: item.refs,
                });
                if (item.kind === "gen_ui") newMsgs.push({role: "gen_ui", payload: item.payload});
            }
            if (note) {
                let noted = false;
                for (let i = newMsgs.length - 1; i >= 0; i--) {
                    if (newMsgs[i].role === "assistant") {
                        (newMsgs[i] as { role: "assistant"; content: string }).content += `\n\n*${note}*`;
                        noted = true;
                        break;
                    }
                }
                if (!noted) newMsgs.push({role: "assistant", content: `*${note}*`});
            }
            if (newMsgs.some(m => m.role === "assistant")) gotReply = true;
            nextHistory = [...nextHistory, ...newMsgs];
            liveFlowArr.length = 0;
            return true;
        };

        try {
            while (true) {
                let cleanExit = false;
                const abort = new AbortController();
                // currentStream.current can be null here if a concurrent
                // send() / clear() raced with this resume loop and cleared
                // it (the previous turn's finally ran while we were
                // awaiting backoff). Drop out cleanly instead of throwing
                // an unhandled "Cannot set properties of null".
                const cs = currentStream.current;
                if (!cs || cs.stopped || cs.discarded) break;
                cs.abort = abort;

                try {
                    const url = `${base}/chat/stream?message_id=${encodeURIComponent(messageId)}&since=${sinceOffset}`;
                    // sinceOffset===0 on the very first attempt carries firstBody
                    // (which may be the user message OR "{}" for a pure resume).
                    // Reconnects after that always send "{}" to avoid double-appending.
                    const body = sinceOffset === 0 ? firstBody : "{}";
                    const res = await fetch(url, {
                        method: "POST",
                        headers: {"content-type": "application/json"},
                        body,
                        signal: abort.signal,
                        // credentials:"include" lets the browser ship the
                        // propwise_session cookie cross-origin (FE on
                        // propwise.algonavis.com, BE on the same host but
                        // potentially a different path during local dev).
                        // Without it the BE cannot identify the caller's
                        // agent role, so list_my_* tools surface
                        // NOT_AUTHENTICATED even when the user is logged in.
                        credentials: "include",
                    });
                    if (!res.ok) {
                        const errBody = await res.json().catch(() => ({error: `HTTP ${res.status}`}));
                        terminalError = errBody.error || `HTTP ${res.status}`;
                        cleanExit = true;
                        break;
                    }
                    const xConv = res.headers.get("X-Conversation-Id");
                    if (xConv && xConv !== conversationIdRef.current) {
                        const isNew = conversationIdRef.current === null;
                        conversationIdRef.current = xConv;
                        saveConversationId(mode, xConv);
                        if (isNew) {
                            window.dispatchEvent(
                                new CustomEvent(STORAGE_EVENTS.conversationCreated, {detail: {id: xConv}})
                            );
                        }
                    }
                    if (conversationIdRef.current) {
                        savePendingStream(mode, {conversationId: conversationIdRef.current, messageId});
                    }

                    const reader = res.body!.getReader();
                    const decoder = new TextDecoder();
                    let buf = "";
                    let event = "";
                    let id = "";

                    while (true) {
                        const {done, value} = await reader.read();
                        if (done) break;
                        buf += decoder.decode(value, {stream: true});
                        // Same race-safety as the abort write — if a
                        // concurrent clear() nulled currentStream while
                        // we were awaiting the next chunk, drop out.
                        if (currentStream.current) {
                            currentStream.current.lastDataAt = Date.now();
                            currentStream.current.gotChunk = true;
                        }

                        const lines = buf.split("\n");
                        buf = lines.pop() ?? "";

                        for (const line of lines) {
                            if (line === "" || line.startsWith(":")) continue;
                            if (line.startsWith("id: ")) {
                                id = line.slice(4).trim();
                                continue;
                            }
                            if (line.startsWith("event: ")) {
                                event = line.slice(7).trim();
                                continue;
                            }
                            if (!line.startsWith("data: ")) continue;

                            let payload: {
                                delta?: string;
                                name?: string;
                                label_key?: string;
                                input?: unknown;
                                output?: string;
                                message?: string;
                                tool_use_id?: string;
                                kind?: string;
                                options?: ChoiceOption[];
                                multiple?: boolean;
                                gen_ui?: unknown;
                            };
                            try {
                                payload = JSON.parse(line.slice(6));
                            } catch {
                                continue;
                            }

                            if (id) {
                                const n = parseInt(id, 10);
                                if (!Number.isNaN(n) && n > sinceOffset) sinceOffset = n;
                            }

                            if (event === "text") {
                                const delta = payload.delta || "";
                                if (delta) {
                                    const last = liveFlowArr.length > 0 ? liveFlowArr[liveFlowArr.length - 1] : null;
                                    if (last?.kind === "text") {
                                        last.content += delta;
                                    } else {
                                        if (last?.kind === "thinking") liveFlowArr.pop();
                                        liveFlowArr.push({kind: "text", content: delta});
                                    }
                                    setLiveFlow([...liveFlowArr]);
                                }
                            } else if (event === "tool_call") {
                                const last = liveFlowArr.length > 0 ? liveFlowArr[liveFlowArr.length - 1] : null;
                                if (last?.kind === "thinking") liveFlowArr.pop();
                                liveFlowArr.push({kind: "active", name: payload.name || "tool"});
                                setLiveFlow([...liveFlowArr]);
                            } else if (event === "tool_result") {
                                // Promote the matching "active" spinner to a completed "tool" step.
                                // Extract project mentions + web-search refs so the UI can render
                                // ProjectChipRow + ReviewChipRow next to this turn.
                                const projects = extractProjects(payload.name || "", payload.output || "");
                                const projectsField = projects.length > 0 ? projects : undefined;
                                const refs = extractReviewRefs(payload.name || "", payload.output || "");
                                const refsField = refs.length > 0 ? refs : undefined;
                                let found = false;
                                for (let i = liveFlowArr.length - 1; i >= 0; i--) {
                                    if (liveFlowArr[i].kind === "active") {
                                        liveFlowArr[i] = {kind: "tool", name: payload.name || "tool", projects: projectsField, refs: refsField};
                                        found = true;
                                        break;
                                    }
                                }
                                if (!found) liveFlowArr.push({kind: "tool", name: payload.name || "tool", projects: projectsField, refs: refsField});
                                setLiveFlow([...liveFlowArr]);
                            } else if (event === "gen_ui") {
                                // Structured UI component emitted by the present_data tool.
                                if (isValidGenUI(payload.gen_ui)) {
                                    // Remove the thinking spinner if still present.
                                    const last = liveFlowArr.length > 0 ? liveFlowArr[liveFlowArr.length - 1] : null;
                                    if (last?.kind === "thinking") liveFlowArr.pop();
                                    liveFlowArr.push({kind: "gen_ui", payload: payload.gen_ui});
                                    setLiveFlow([...liveFlowArr]);
                                }
                            } else if (event === "done") {
                                const newMsgs: ChatMessage[] = [];
                                for (const item of liveFlowArr) {
                                    if (item.kind === "text") newMsgs.push({role: "assistant", content: item.content});
                                    if (item.kind === "tool") newMsgs.push({
                                        role: "tool_trace",
                                        name: item.name,
                                        inputSummary: "",
                                        outputSummary: "",
                                        projects: item.projects,
                                        refs: item.refs,
                                    });
                                    if (item.kind === "gen_ui") newMsgs.push({role: "gen_ui", payload: item.payload});
                                }
                                if (!newMsgs.some(m => m.role === "assistant" || m.role === "gen_ui")) {
                                    newMsgs.push({role: "assistant", content: "(no reply)"});
                                }
                                nextHistory = [...nextHistory, ...newMsgs];
                                gotReply = true;
                                liveFlowArr.length = 0;
                                setLiveFlow([]);
                                cleanExit = true;
                            } else if (event === "waiting_for_user") {
                                // Pause. Kind distinguishes the flavour:
                                //   hitl              — free-text; prompt folded into the assistant bubble.
                                //   hitl_choice       — structured options; shown as HITLChoiceCard.
                                //   hitl_form         — multi-question form; shown as HITLFormCard.
                                //   approval          — control-plane notice; lives in the composer badge.
                                //   workspace_propose — chat-side workspace mutation proposal;
                                //                       rendered as an Add/Skip-style chip per tool_name.
                                const kind =
                                    payload.kind === "approval" ? "approval" :
                                        payload.kind === "hitl_choice" ? "hitl_choice" :
                                            payload.kind === "hitl_form" ? "hitl_form" :
                                                payload.kind === "workspace_propose" ? "workspace_propose" : "hitl";
                                const prompt = payload.message || "";
                                // hitl_choice/hitl_form/workspace_propose carry the structured
                                // payload as Input (raw JSON of the tool_use input). Re-parse to
                                // recover allow_other / questions / propose args without a
                                // round-trip.
                                let parsedInput: {
                                    options?: ChoiceOption[];
                                    multiple?: boolean;
                                    allow_other?: boolean;
                                    title?: string;
                                    reason?: string;
                                    questions?: FormQuestion[];
                                    [key: string]: unknown;
                                } = {};
                                if (payload.input) {
                                    if (typeof payload.input === "string") {
                                        try { parsedInput = JSON.parse(payload.input); } catch { /* ignore */ }
                                    } else if (typeof payload.input === "object") {
                                        parsedInput = payload.input as typeof parsedInput;
                                    }
                                }
                                const newMsgs: ChatMessage[] = [];
                                for (const item of liveFlowArr) {
                                    // For hitl_choice / hitl_form the proactive card owns the
                                    // prompt text. workspace_propose chips render below the
                                    // assistant text so streaming text IS shown for them.
                                    if (item.kind === "text" && kind !== "hitl_choice" && kind !== "hitl_form") {
                                        newMsgs.push({role: "assistant", content: item.content});
                                    }
                                    if (item.kind === "tool") newMsgs.push({
                                        role: "tool_trace",
                                        name: item.name,
                                        inputSummary: "",
                                        outputSummary: "",
                                        projects: item.projects,
                                        refs: item.refs,
                                    });
                                    if (item.kind === "gen_ui") newMsgs.push({role: "gen_ui", payload: item.payload});
                                }
                                if (kind === "hitl") {
                                    // Append HITL prompt to last assistant bubble or create one.
                                    let appended = false;
                                    for (let i = newMsgs.length - 1; i >= 0; i--) {
                                        if (newMsgs[i].role === "assistant") {
                                            const m = newMsgs[i] as { role: "assistant"; content: string };
                                            m.content = m.content + (m.content && prompt ? "\n\n" : "") + prompt;
                                            appended = true;
                                            break;
                                        }
                                    }
                                    if (!appended && prompt) newMsgs.push({role: "assistant", content: prompt});
                                    if (!newMsgs.some(m => m.role === "assistant")) {
                                        newMsgs.push({role: "assistant", content: "(awaiting your input)"});
                                    }
                                }
                                nextHistory = [...nextHistory, ...newMsgs];
                                gotReply = true;
                                liveFlowArr.length = 0;
                                setLiveFlow([]);
                                setPending({
                                    toolUseId: payload.tool_use_id || "",
                                    kind,
                                    toolName: payload.name,
                                    prompt,
                                    options: kind === "hitl_choice"
                                        ? ((payload.options as ChoiceOption[] | undefined) ?? parsedInput.options)
                                        : undefined,
                                    multiple: kind === "hitl_choice"
                                        ? ((payload.multiple as boolean | undefined) ?? parsedInput.multiple)
                                        : undefined,
                                    allowOther: kind === "hitl_choice" ? !!parsedInput.allow_other : undefined,
                                    formTitle: kind === "hitl_form" ? parsedInput.title : undefined,
                                    formReason: kind === "hitl_form" ? parsedInput.reason : undefined,
                                    formQuestions: kind === "hitl_form" ? parsedInput.questions : undefined,
                                    proposeArgs: kind === "workspace_propose" ? parsedInput : undefined,
                                });
                                cleanExit = true;
                            } else if (event === "error") {
                                if (!commitPartial("(response interrupted — send a follow-up to continue)")) {
                                    terminalError = payload.message || "Stream error";
                                }
                                cleanExit = true;
                            }
                            event = "";
                            id = "";
                        }
                        if (cleanExit) break;
                    }
                } catch (err) {
                    void err;
                }

                if (cleanExit) break;
                if (currentStream.current?.stopped) {
                    commitPartial("(stopped)");
                    break;
                }
                resumeAttempts++;
                if (resumeAttempts > MAX_RESUMES) {
                    if (!commitPartial("(connection lost — send a follow-up to continue)")) {
                        terminalError = "Connection lost. Please send a follow-up to continue.";
                    }
                    break;
                }
                const backoff = Math.min(300 * 2 ** (resumeAttempts - 1), 3000);
                await new Promise((r) => setTimeout(r, backoff));
            }
        } finally {
            // Read the discard flag BEFORE we clear currentStream — clear()
            // may have set discarded=true to tell us this thread is being
            // thrown away (e.g. user hit "+ New chat" mid-stream).
            const discarded = currentStream.current?.discarded === true;
            currentStream.current = null;
            savePendingStream(mode, null);
            // Surface terminalError to send() so it can decide on a quiet
            // auto-retry for transient proxy stalls.
            if (terminalError) lastTerminalErrorRef.current = terminalError;
            if (!gotReply && terminalError && !discarded) {
                if (!commitPartial("(response interrupted — send a follow-up to continue)")) {
                    nextHistory = [...nextHistory, {role: "error", content: terminalError}];
                }
            }
            // When the stream was discarded, history was just reset to []
            // by clear() — DON'T flush nextHistory back over it.
            if (!discarded) setHistory(nextHistory);
            liveFlowArr.length = 0;
            setLiveFlow([]);
            setSending(false);

            // Background re-hydrate: patches DB block IDs onto assistant messages
            // so feedback buttons can reference them immediately after streaming.
            // We patch IDs into the local nextHistory snapshot rather than replacing
            // history wholesale — a full replacement races with the server DB commit
            // and can arrive before tool_trace rows (gen_ui) are persisted, causing
            // gen_ui components to vanish until the next page load.
            if (!discarded && gotReply && conversationIdRef.current) {
                const convId = conversationIdRef.current;
                const historySnapshot = nextHistory;
                apiFetch(`/conversations/${encodeURIComponent(convId)}/messages`)
                    .then((r) => (r.ok ? r.json() : null))
                    .then((body: { messages?: ServerMessage[] } | null) => {
                        if (!body?.messages) return;
                        if (conversationIdRef.current !== convId) return;
                        // Extract IDs from server assistant messages in sequence order.
                        const serverAssistants = body.messages.filter(
                            (m): m is ServerMessage & { id: string } =>
                                m.role === "assistant" && !!m.id
                        );
                        if (serverAssistants.length === 0) return;
                        let si = 0;
                        const patched = historySnapshot.map((m) => {
                            if (m.role !== "assistant") return m;
                            const sm = serverAssistants[si];
                            if (sm) { si++; return { ...m, id: sm.id }; }
                            return m;
                        });
                        setHistory(patched);
                    })
                    .catch(() => { /* silent */ });
            }
        }

        return gotReply;
    }, [mode]);

    // Hydrate history and, if the last persisted turn is an unanswered
    // user message matching a pending stream, rejoin the in-flight
    // producer so the user sees the reply stream in without a manual reload.
    // Also fetch /pending so a HITL pause that landed while we were away
    // surfaces its composer-routing state on mount.
    useEffect(() => {
        const id = conversationIdRef.current;
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const [msgsRes, pendingRes] = await Promise.all([
                    apiFetch(`/conversations/${encodeURIComponent(id)}/messages`),
                    apiFetch(`/conversations/${encodeURIComponent(id)}/pending`).catch(() => null),
                ]);
                if (!msgsRes.ok) {
                    if (msgsRes.status === 404) {
                        conversationIdRef.current = null;
                        saveConversationId(mode, null);
                        savePendingStream(mode, null);
                    }
                    return;
                }
                const body = (await msgsRes.json()) as { messages?: ServerMessage[] };
                if (cancelled || !body.messages) return;
                const hydrated = hydrateFromServer(body.messages);
                setHistory(hydrated);

                let hasPending = false;
                if (pendingRes && pendingRes.ok) {
                    const p = pendingFromResponse((await pendingRes.json()) as PendingResponse);
                    if (p) {
                        setPending(p);
                        hasPending = true;
                    }
                }

                const pendingStream = loadPendingStream(mode);
                if (!pendingStream || pendingStream.conversationId !== id) {
                    if (pendingStream) savePendingStream(mode, null);
                    return;
                }
                if (hasPending) {
                    // Producer already finished by emitting waiting_for_user — no
                    // live stream to rejoin. Drop the marker so stop() stays inert.
                    savePendingStream(mode, null);
                    return;
                }
                if (!lastNonTraceIsUser(hydrated)) {
                    // BE already finished and persisted while we were away.
                    savePendingStream(mode, null);
                    return;
                }
                // Detached producer may still be running; empty body tells BE
                // to tail by message_id instead of starting a new turn.
                void runStream({messageId: pendingStream.messageId, firstBody: "{}", baseline: hydrated});
            } catch {
                // Network failure — leave history empty; next send still works.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [runStream, mode]);

    const send = useCallback(async (text: string): Promise<boolean> => {
        if (!text.trim()) return false;
        // Guard: if a stream is already in flight, don't fire a new
        // runStream — two parallel SSE readers race on currentStream.current
        // and produce mangled history (we saw "word1\nword2" merged into
        // one user bubble with a "word1word1" reply). Composer + tray
        // onAsk paths already block the button, but programmatic callers
        // (and now the IME-fix retry on Safari) need a hook-level guard.
        if (currentStream.current) return false;
        const userMsg: ChatMessage = {role: "user", content: text.trim()};
        const baseline = [...history, userMsg];
        setHistory(baseline);

        const buildBody = () => JSON.stringify({
            conversation_id: conversationIdRef.current || undefined,
            message: text.trim(),
            mode,
            // user_id intentionally NOT sent — the backend ignores
            // client-supplied ownership claims and resolves the caller
            // from cookies (propwise_session for signed-in users,
            // propwise_anon_session for anonymous browsers). Sending it
            // was both unused and historically part of an IDOR surface.
            workspace_id: workspaceId || undefined,
        });

        const ok = await runStream({messageId: newMessageId(), firstBody: buildBody(), baseline});
        if (ok) return true;

        // Auto-retry on upstream stall ("took too long" / "did not
        // respond"). The new-api proxy occasionally buffer-stalls and
        // the 90s idle watchdog converts that into a friendly error.
        // The user shouldn't have to manually re-send for what's almost
        // always a transient proxy hiccup. One quiet retry catches the
        // common case; if both fail, the error stands.
        const latest = lastTerminalErrorRef.current;
        const stallish = /took too long|did not respond|connection.*interrupted/i.test(latest);
        if (!stallish) return false;
        // Remove the stall error row so it's not a flicker visible behind
        // the retry, then re-baseline (history + same user msg).
        setHistory((h) => h.filter((m) => !(m.role === "error" && /took too long|did not respond|connection.*interrupted/i.test(m.content))));
        // Short pause so the proxy can shed its previous in-flight slot
        // before we open a new one — back-to-back creates correlate with
        // a second stall in observation.
        await new Promise((r) => setTimeout(r, 800));
        return runStream({messageId: newMessageId(), firstBody: buildBody(), baseline});
    }, [history, mode, runStream, workspaceId]);

    // sendResolve posts to /chat/respond and tails the continuation
    // producer. Used by both the free-text respond() path (hitl) and the
    // approve()/deny() paths (approval). userBubble is the transcript
    // entry to commit for the user's side of the exchange — a plain user
    // message for hitl, a synthetic "(approved)" or "(denied: …)" line
    // for approval so the transcript has a visible trace of the decision.
    const sendResolve = useCallback(async (opts: {
        userBubble: ChatMessage | null;
        body: Record<string, unknown>;
    }): Promise<boolean> => {
        const convID = conversationIdRef.current;
        const p = pending;
        if (!convID || !p) return false;

        const baseline = opts.userBubble ? [...history, opts.userBubble] : history;
        if (opts.userBubble) setHistory(baseline);
        setPending(null);

        const messageId = newMessageId();
        const restorePending = () => setPending(p);
        try {
            const res = await apiFetch("/chat/respond", {
                method: "POST",
                headers: {"content-type": "application/json"},
                body: JSON.stringify({
                    conversation_id: convID,
                    tool_use_id: p.toolUseId,
                    message_id: messageId,
                    ...opts.body,
                }),
            });
            if (res.ok && p.kind === "workspace_propose" && opts.userBubble?.role === "propose_result") {
                // Backend dispatched the propose synchronously and echoed
                // the result (applied / property_id / destination_workspace_id)
                // in the response body. Patch the just-committed
                // propose_result row so the chip's frozen state shows the
                // correct status + dossier link immediately — without
                // waiting for /messages hydration after the continuation
                // producer streams the LLM's follow-up reply.
                try {
                    const resp = (await res.clone().json()) as {
                        propose_result?: {
                            applied?: string;
                            property_id?: string;
                            destination_workspace_id?: string;
                        };
                    };
                    const pr = resp?.propose_result;
                    if (pr) {
                        setHistory((prev) => {
                            // Patch the most recent propose_result row that
                            // matches this resolution (toolUseId stays unique
                            // per chip so any prior frozen rows are untouched).
                            for (let i = prev.length - 1; i >= 0; i--) {
                                const m = prev[i];
                                if (m.role === "propose_result") {
                                    const next = prev.slice();
                                    next[i] = {
                                        ...m,
                                        applied: pr.applied ?? m.applied,
                                        propertyId: pr.property_id ?? m.propertyId,
                                        destinationWorkspaceId: pr.destination_workspace_id ?? m.destinationWorkspaceId,
                                    };
                                    return next;
                                }
                            }
                            return prev;
                        });
                    }
                } catch { /* response wasn't JSON — fall back to hydration */ }
            }
            if (!res.ok) {
                const err = await res.json().catch(() => ({error: `HTTP ${res.status}`}));
                setHistory([...baseline, {role: "error", content: err.error || `HTTP ${res.status}`}]);
                // 409 means the pending row is gone (retried past it, already
                // resolved, etc.) — the pause is no longer live, so don't restore.
                if (res.status !== 409) restorePending();
                return false;
            }
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Network error";
            setHistory([...baseline, {role: "error", content: msg}]);
            restorePending();
            return false;
        }

        return runStream({messageId, firstBody: "{}", baseline});
    }, [history, pending, runStream]);

    // respond resolves a HITL pause with the user's free-form answer.
    const respond = useCallback(async (text: string): Promise<boolean> => {
        const body = text.trim();
        if (!body) return false;
        return sendResolve({
            userBubble: {role: "user", content: body},
            body: {response: body},
        });
    }, [sendResolve]);

    // respondChoice resolves a hitl_choice OR workspace_propose pause with
    // one or more selected option values. otherText carries the user-typed
    // string when CHOICE_OTHER_VALUE is in `selected` (kind=hitl_choice with
    // allow_other=true; not used by workspace_propose).
    //
    // workspaceId, when provided on a workspace_propose pause, overrides
    // the conversation's linked workspace as the destination of the
    // mutation. The backend validates ownership.
    const respondChoice = useCallback(async (selected: string[], otherText?: string, workspaceId?: string): Promise<boolean> => {
        if (selected.length === 0) return false;
        const p = pending;
        if (!p) return false;

        // workspace_propose has no client-side options array — the chip
        // owns its own button copy keyed by toolName. Commit a propose_result
        // userBubble immediately so the resolved chip renders in the
        // thread (mirroring how /messages hydration represents it once the
        // backend persists the tool_result).
        if (p.kind === "workspace_propose") {
            const value = selected[0] ?? "";
            const body: Record<string, unknown> = { selected };
            const ws = (workspaceId ?? "").trim();
            if (ws) body.workspace_id = ws;
            return sendResolve({
                userBubble: {
                    role: "propose_result",
                    toolName: p.toolName ?? "",
                    args: p.proposeArgs ?? {},
                    selected: value,
                    applied: "", // filled by backend on hydration; "" is fine for the live commit
                },
                body,
            });
        }

        const labels = selected
            .map((v) => {
                if (v === CHOICE_OTHER_VALUE) {
                    const txt = (otherText ?? "").trim();
                    return txt ? `Other: ${txt}` : "Other";
                }
                return p.options?.find((o) => o.value === v)?.label ?? v;
            })
            .join(", ");
        const trimmedOther = (otherText ?? "").trim();
        // Use choice_result (not a plain user bubble) so the card renders
        // immediately in history — matching what page-load hydration produces.
        return sendResolve({
            userBubble: {
                role: "choice_result",
                content: labels,
                prompt: p.prompt ?? "",
                options: p.options ?? [],
                selected,
                otherText: trimmedOther || undefined,
            },
            body: {
                selected,
                display_text: labels,
                ...(trimmedOther ? {other_text: trimmedOther} : {}),
            },
        });
    }, [pending, sendResolve]);

    // respondForm resolves a hitl_form pause with a per-question answers map.
    const respondForm = useCallback(async (answers: FormAnswers): Promise<boolean> => {
        const p = pending;
        if (!p || p.kind !== "hitl_form") return false;
        const questions = p.formQuestions ?? [];
        if (questions.length === 0) return false;
        const display = renderFormDisplay(questions, answers);
        return sendResolve({
            userBubble: {
                role: "form_result",
                content: display,
                title: p.formTitle,
                reason: p.formReason,
                questions,
                answers,
            },
            body: {answers, display_text: display},
        });
    }, [pending, sendResolve]);

    // approve resolves an approval pause by letting the backend dispatch
    // the stashed tool. Optional reason is passed through as Response and
    // currently ignored by the backend on approve — it exists so UIs can
    // capture a note for the audit trail without changing the API shape.
    const approve = useCallback(async (reason?: string): Promise<boolean> => {
        return sendResolve({
            userBubble: {role: "user", content: reason?.trim() ? `(approved: ${reason.trim()})` : "(approved)"},
            body: {approved: true, response: reason ?? ""},
        });
    }, [sendResolve]);

    // deny resolves an approval pause by feeding back a DENIED
    // tool_result. The reason (if any) surfaces to the model as the
    // error_message so it can acknowledge the rejection.
    const deny = useCallback(async (reason?: string): Promise<boolean> => {
        const trimmed = reason?.trim() || "";
        return sendResolve({
            userBubble: {role: "user", content: trimmed ? `(denied: ${trimmed})` : "(denied)"},
            body: {approved: false, response: trimmed},
        });
    }, [sendResolve]);

    const retry = useCallback(async (): Promise<boolean> => {
        if (sending) return false;
        let lastUserIdx = -1;
        for (let i = history.length - 1; i >= 0; i--) {
            if (history[i].role === "user") {
                lastUserIdx = i;
                break;
            }
        }
        if (lastUserIdx < 0) return false;
        const lastUser = history[lastUserIdx] as { role: "user"; content: string };
        const baseline = history.slice(0, lastUserIdx + 1);
        setHistory(baseline);
        // Retrying past a HITL pause means the user chose not to answer it.
        // BE cancels the pending row inside resolveChat; mirror that locally.
        setPending(null);

        const messageId = newMessageId();
        const convID = conversationIdRef.current;
        const firstBody = JSON.stringify({
            conversation_id: convID || undefined,
            message: lastUser.content,
            mode,
            // Only ask the BE to skip re-appending the user message when we
            // actually have a conversation on the server. Without one, retry
            // acts as a normal fresh send.
            retry: !!convID,
        });
        return runStream({messageId, firstBody, baseline});
    }, [history, mode, runStream, sending]);

    const stop = useCallback(() => {
        const cs = currentStream.current;
        if (!cs) return;
        cs.stopped = true;
        const id = cs.id;
        // Tell the BE to stop producing — without this, the detached producer
        // goroutine keeps burning model tokens even after the browser aborts.
        const base = getApiBase();
        void fetch(`${base}/chat/stream/cancel?message_id=${encodeURIComponent(id)}`, {
            method: "POST",
            keepalive: true,
            credentials: "include",
        }).catch(() => { /* best-effort */
        });
        cs.abort?.abort();
    }, []);

    // gen_ui messages are visual side-effects of an assistant reply, not
    // separate replies — counting them double-bumps the lead-capture
    // threshold. Only count actual assistant text replies.
    const assistantReplyCount = history.filter((m) => m.role === "assistant").length;
    const canRetry = !sending && history.some((m) => m.role === "user");

    return {
        history, liveFlow, sending, pending,
        conversationId: conversationIdRef.current,
        workspaces, currentWorkspaceId,
        send, respond, respondChoice, respondForm, approve, deny, stop, clear, open, retry, canRetry, assistantReplyCount,
    };
}

// renderFormDisplay produces a short multi-line label that's shown both in
// the user-bubble of a freshly resolved hitl_form pause and the persisted
// form_result row. Mirrors the BE's display_text serialization so live and
// hydrated views look identical.
function renderFormDisplay(questions: FormQuestion[], answers: FormAnswers): string {
    const lines: string[] = [];
    for (const q of questions) {
        const a = answers[q.id];
        if (!a) continue;
        let body = "";
        if (q.type === "text") {
            body = (a as FormAnswerText).text ?? "";
        } else if (q.type === "single_choice") {
            const v = (a as FormAnswerSingle).value;
            const ot = (a as FormAnswerSingle).other_text;
            if (v === CHOICE_OTHER_VALUE) {
                body = ot ? `Other: ${ot}` : "Other";
            } else {
                body = q.options?.find((o) => o.value === v)?.label ?? v ?? "";
            }
        } else if (q.type === "multi_choice") {
            const vs = (a as FormAnswerMulti).values ?? [];
            const ot = (a as FormAnswerMulti).other_text;
            const labels = vs.map((v) => {
                if (v === CHOICE_OTHER_VALUE) return ot ? `Other: ${ot}` : "Other";
                return q.options?.find((o) => o.value === v)?.label ?? v;
            });
            body = labels.join(", ");
        }
        if (body) lines.push(`${q.prompt}: ${body}`);
    }
    return lines.join("\n");
}
